/* On-screen Emirates tracking (Packing List / Bulk Data preview): double-click an Emirates TRACK NO. cell, or
   download → fill → upload the tracking sheet. Temporary EMI… numbers save freely; real numbers need a 3-hour unlock. */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileSpreadsheet, Upload } from "lucide-react";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TEMP = /^EMI\d{5,7}$/i;
const swapIn = (data, swaps) => {
  const fix = (rows) => (rows || []).map((r) => r.map((c) => (swaps[String(c ?? "").trim().toUpperCase()] ?? c)));
  return { ...data, rows: fix(data.rows), sheets: (data.sheets || []).map((s) => ({ ...s, rows: fix(s.rows) })) };
};

export const useEmiTracking = (module, entryId, enabled, setPreviewData) => {
  const [tracks, setTracks] = useState(new Set());
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lockFor, setLockFor] = useState(null);
  const base = `${API}/onscreen-tracking/${module}/${entryId}`;
  useEffect(() => {
    if (!enabled) return;
    axios.get(`${base}/tracks`, { headers: getAuthHeader() }).then((r) => setTracks(new Set(r.data.tracks))).catch(() => {});
  }, [base, enabled]);
  const applySwaps = useCallback((applied) => {
    const swaps = Object.fromEntries(applied.map((a) => [a.old, a.new]));
    setPreviewData((d) => (d ? swapIn(d, swaps) : d));
    setTracks((t) => new Set([...t].map((x) => swaps[x] || x)));
  }, [setPreviewData]);
  const isEmi = (t) => { const u = String(t || "").trim().toUpperCase(); return !!u && (tracks.has(u) || TEMP.test(u)); };
  const save = async (old, value) => {
    const nv = String(value || "").trim().toUpperCase();
    if (!nv || nv === old.toUpperCase()) { setEdit(null); return; }
    setSaving(true);
    try {
      const r = await axios.post(base, { old, new: nv }, { headers: getAuthHeader() });
      applySwaps(r.data.applied);
      toast.success(`Tracking ${old} → ${nv} saved (Order Register, Database A, ${r.data.files_patched} file(s))`);
      setEdit(null);
    } catch (e) {
      if (e.response?.status === 403 && /locked/i.test(e.response?.data?.detail || "")) setLockFor({ old, value: nv });
      else toast.error(e.response?.data?.detail || "Tracking save failed");
    } finally { setSaving(false); }
  };
  return { tracks, isEmi, edit, setEdit, saving, save, lockFor, setLockFor, applySwaps, base, hasAny: tracks.size > 0 };
};

export const TrackLockDialog = ({ emi }) => {
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/tracking-edit/unlock`, { password: pwd }, { headers: getAuthHeader() });
      toast.success("Tracking changes unlocked for 3 hours");
      const p = emi.lockFor; emi.setLockFor(null); setPwd("");
      if (p) emi.save(p.old, p.value);
    } catch (e) { toast.error(e.response?.data?.detail || "Unlock failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!emi.lockFor} onOpenChange={(o) => !o && emi.setLockFor(null)}>
      <DialogContent className="max-w-sm" data-testid="emi-lock-dialog">
        <DialogHeader><DialogTitle>Change a real tracking number?</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">{emi.lockFor?.old} already has a real number. Enter your login password — changes stay open for 3 hours.</p>
        <input type="password" autoFocus value={pwd} onChange={(e) => setPwd(e.target.value)} onKeyDown={(e) => e.key === "Enter" && go()}
          className="h-9 rounded-md border px-3 text-sm" placeholder="Your password" data-testid="emi-lock-password" />
        <Button onClick={go} disabled={busy || !pwd} data-testid="emi-lock-submit">Unlock & save</Button>
      </DialogContent>
    </Dialog>
  );
};

export const TrackSheetButtons = ({ emi }) => {
  const ref = useRef(null);
  const download = async () => {
    try {
      const r = await axios.get(`${emi.base}/template`, { headers: getAuthHeader(), responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = (r.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || "EMIRATES_TRACKING.xlsx";
      a.click();
      toast.success("Tracking sheet downloaded — fill NEW TRACK NO. and upload it back");
    } catch { toast.error("Tracking sheet download failed"); }
  };
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${emi.base}/upload`, fd, { headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" } });
      const d = r.data;
      emi.applySwaps(d.applied);
      const locked = d.skipped.filter((s) => s.reason === "LOCKED").length;
      const other = d.skipped.filter((s) => s.reason !== "LOCKED");
      toast.success(`Tracking updated for ${d.applied.length} parcel(s)`
        + (locked ? ` · ${locked} real number(s) need your password — double-click those cells` : "")
        + (other.length ? ` · skipped: ${other.slice(0, 3).map((s) => `${s.old} (${s.reason})`).join(", ")}` : ""), { duration: 9000 });
    } catch (e) { toast.error(e.response?.data?.detail || "Tracking upload failed"); }
  };
  return (
    <>
      <Button size="sm" variant="outline" className="border-rose-500 text-rose-700 hover:bg-rose-50" onClick={download}
        title="Download the Emirates tracking sheet (CURRENT → NEW TRACK NO.)" data-testid="emi-track-template-btn">
        <FileSpreadsheet className="w-4 h-4 mr-1.5" />Tracking Sheet</Button>
      <Button size="sm" variant="outline" className="border-rose-500 text-rose-700 hover:bg-rose-50" onClick={() => ref.current?.click()}
        title="Upload the filled Emirates tracking sheet" data-testid="emi-track-upload-btn">
        <Upload className="w-4 h-4 mr-1.5" />Upload Tracking</Button>
      <input ref={ref} type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }}
        data-testid="emi-track-upload-input" />
    </>
  );
};
